import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import HeroSection from "@/components/HeroSection";
import ThreePillars from "@/components/ThreePillars";
import VideoTile from "@/components/VideoTile";
import {
  SERVICES,
  SERVICE_TAGLINES,
  HOW_IT_WORKS,
  WHY_REASONS,
  REACH_STATS_HEADLINE,
  POSITIONS,
} from "@/lib/content";
import { CASE_STUDIES } from "@/lib/caseStudies";
import { ArrowUpRight, Quote, Check, Newspaper } from "lucide-react";

const NEWS_FEED_URL =
  "https://api.rss2json.com/v1/api.json?rss_url=https%3A%2F%2Fwww.highwaysindustry.com%2Fcategory%2Ffeatures%2Ffeed%2F";

export default function Home() {
  const [news, setNews] = useState([]);
  const [newsError, setNewsError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(NEWS_FEED_URL)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (data.status !== "ok" || !Array.isArray(data.items)) {
          setNewsError(true);
          return;
        }
        const withImages = data.items.map((item) => {
          if (item.thumbnail) return item;
          const html = item.content || item.description || "";
          const match = html.match(/<img[^>]+src=["'](https?:\/\/[^"'>]+)/i);
          return {
            ...item,
            thumbnail: match ? match[1] : "",
          };
        });
        setNews(withImages.slice(0, 3));
      })
      .catch(() => {
        if (!cancelled) setNewsError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const formatDate = (iso) => {
    try {
      return new Date(iso.replace(" ", "T") + "Z").toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "";
    }
  };

  return (
    <main data-testid="home-page" className="bg-white">
      <Seo
        title="Create. Amplify. Position."
        description="HighwaysIndustry.com is the media and content platform built for the UK highways and infrastructure sector. Video, editorial, brand-building and industry reach from the specialists who know the sector inside out."
        keywords="highways marketing agency, infrastructure branding, highways media, road sector marketing, UK highways industry, highways video production, highways PR, highways content"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          "name": "HighwaysIndustry.com",
          "description": "Content, media, video and brand-building built for the UK highways and infrastructure sector.",
          "url": "https://whyhighwaysindustry.com",
          "logo": "https://whyhighwaysindustry.com/highways-industry-mark.png",
          "image": "https://whyhighwaysindustry.com/og-image.jpg",
          "priceRange": "£££",
          "areaServed": {
            "@type": "Country",
            "name": "United Kingdom",
          },
          "serviceType": [
            "Content marketing",
            "Video production",
            "Photography",
            "Branding",
            "Editorial",
            "Industry directory",
            "PR",
          ],
        }}
      />
      <HeroSection />

      {/* Client logo strip, light */}
      <section
        data-testid="authority-strip"
        className="border-y border-slate-200 bg-slate-50 py-6 overflow-hidden"
      >
        <div className="flex items-center gap-12 whitespace-nowrap animate-marquee">
          {[
            "ACO",
            "Blakedale",
            "Blinc Light",
            "BT-HS",
            "Cappagh",
            "CD Fencing",
            "Chellan Highway Safety Services",
            "City TM",
            "Coeval",
            "Consilium Technical Services",
            "Core Highways",
            "Crown Highways",
            "Crown Plus",
            "DELTABLOC UK",
            "Highway Care",
            "Hill & Smith Infrastructure",
            "HRS",
            "HTM",
            "Hireway",
            "Institute of Asphalt Technology",
            "Instarmac",
            "Kiely Bros",
            "Kier Transportation Services",
            "King Highway Products",
            "MAC Surfacing",
            "M Group Telecom",
            "Meon",
            "Messagemaker Displays / Stocksigns Ltd",
            "Metelec Services",
            "Miles Macadam",
            "Milestone Infrastructure",
            "Multevo",
            "National Highways",
            "Nissen UK",
            "PoGo",
            "R&C Williams",
            "Ramudden Global UK (Chevron TM)",
            "Re-flow",
            "Roadway TM",
            "SHIMPAC",
            "Solar Gates",
            "SWARCO",
            "SWARCO Smart Charging",
            "Tailored Highway Support (THS)",
            "Tarmac",
            "THS Training",
            "Thomson Protective Coatings",
            "TMCA",
            "Traffic Group Signals",
            "Traffic Labour Supplies (TLS)",
            "Transport for London",
            "Triangle Software",
            "Up To Speed Training",
            "UK Sandbags",
            "Velocity",
            "VolkerHighways",
            "WDM",
            "WJ",
          ]
            .concat([
              "ACO",
              "Blakedale",
              "Blinc Light",
              "BT-HS",
              "Cappagh",
              "CD Fencing",
              "Chellan Highway Safety Services",
              "City TM",
              "Coeval",
              "Consilium Technical Services",
              "Core Highways",
              "Crown Highways",
              "Crown Plus",
              "DELTABLOC UK",
              "Highway Care",
              "Hill & Smith Infrastructure",
              "HRS",
              "HTM",
              "Hireway",
              "Institute of Asphalt Technology",
              "Instarmac",
              "Kiely Bros",
              "Kier Transportation Services",
              "King Highway Products",
              "MAC Surfacing",
              "M Group Telecom",
              "Meon",
              "Messagemaker Displays / Stocksigns Ltd",
              "Metelec Services",
              "Miles Macadam",
              "Milestone Infrastructure",
              "Multevo",
              "National Highways",
              "Nissen UK",
              "PoGo",
              "R&C Williams",
              "Ramudden Global UK (Chevron TM)",
              "Re-flow",
              "Roadway TM",
              "SHIMPAC",
              "Solar Gates",
              "SWARCO",
              "SWARCO Smart Charging",
              "Tailored Highway Support (THS)",
              "Tarmac",
              "THS Training",
              "Thomson Protective Coatings",
              "TMCA",
              "Traffic Group Signals",
              "Traffic Labour Supplies (TLS)",
              "Transport for London",
              "Triangle Software",
              "Up To Speed Training",
              "UK Sandbags",
              "Velocity",
              "VolkerHighways",
              "WDM",
              "WJ",
            ])
            .map((c, i) => (
              <span
                key={i}
                className="font-heading font-bold text-slate-400 text-sm md:text-base tracking-widest"
              >
                {c}
              </span>
            ))}
        </div>
      </section>

      {/* Our Approach pull-quote CTA */}
      <section
        data-testid="home-approach-cta"
        className="bg-white py-20 md:py-28 border-b border-slate-100"
      >
        <div className="mx-auto max-w-[1200px] px-5 md:px-10">
          <Link
            to="/our-approach"
            data-testid="home-approach-cta-link"
            className="group grid lg:grid-cols-12 gap-8 md:gap-14 items-start"
          >
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3">
                <span className="h-px w-12 bg-accent-blue" />
                <span className="label-eyebrow text-accent-blue">
                  Our approach
                </span>
              </div>
              <p className="mt-6 font-heading font-black text-3xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-slate-900">
                We don&apos;t promise overnight success.
                <br />
                <span className="text-accent-blue italic group-hover:underline decoration-4 underline-offset-8 decoration-cyan-400/60">
                  We build brands that become recognised, trusted and
                  remembered.
                </span>
              </p>
            </div>
            <div className="lg:col-span-4 lg:pt-4 space-y-6">
              <p className="text-slate-600 leading-relaxed text-lg">
                Branding creates demand. Marketing captures it. Read the
                philosophy behind every campaign we build.
              </p>
              <span className="inline-flex items-center gap-2 border border-slate-900 group-hover:bg-slate-900 group-hover:text-white text-slate-900 px-5 py-3 text-sm font-heading font-semibold uppercase tracking-widest transition-colors rounded-full">
                Read our approach
                <ArrowUpRight className="h-4 w-4" />
              </span>
            </div>
          </Link>
        </div>
      </section>

      {/* SECTION 1, What We Actually Do (WHITE) */}
      <section
        data-testid="what-we-actually-do"
        className="py-24 md:py-36 bg-white"
      >
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-accent-blue" />
              <span className="label-eyebrow text-accent-blue">
                What We Actually Do
              </span>
            </div>
            <h2 className="font-heading font-black text-4xl md:text-6xl mt-4 leading-[0.95] tracking-tighter text-slate-900">
              We help highways businesses{" "}
              <span className="text-accent-blue">get seen.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 flex flex-col justify-center">
            <p className="text-xl md:text-2xl text-slate-700 leading-relaxed max-w-2xl">
              We help highways and infrastructure businesses create content,
              reach the right audience and strengthen their brand.
            </p>
            <p className="text-lg md:text-xl text-slate-500 leading-relaxed max-w-2xl mt-6">
              Not through marketing jargon. Through great content and consistent
              visibility.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/what-we-do"
                data-testid="home-services-cta"
                className="inline-flex items-center gap-2 bg-primary-blue text-white px-6 py-3.5 text-sm font-bold uppercase tracking-widest hover:glow-blue transition-all rounded-full"
              >
                See what we do <ArrowUpRight className="h-4 w-4" />
              </Link>
              <Link
                to="/industry-reach"
                className="inline-flex items-center gap-2 border border-slate-300 hover:border-accent-blue text-slate-900 px-6 py-3.5 text-sm font-bold uppercase tracking-widest transition-colors rounded-full"
              >
                Our Industry Reach
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SECTIONS 2-4, Three Pillars (now styled as a soft panel) */}
      <ThreePillars />

      {/* Services tile grid, WHITE */}
      <section
        data-testid="home-services-grid"
        className="bg-white py-24 md:py-32"
      >
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="flex items-center gap-3">
                <span className="h-px w-12 bg-accent-blue" />
                <span className="label-eyebrow text-accent-blue">
                  Our Services
                </span>
              </div>
              <h2 className="font-heading font-black text-4xl md:text-6xl mt-4 leading-[0.95] tracking-tighter text-slate-900 max-w-3xl">
                Real services.
                <br />
                <span className="text-slate-400">Built for your sector.</span>
              </h2>
            </div>
            <Link
              to="/what-we-do"
              className="py-2 text-sm font-bold uppercase tracking-widest text-accent-blue hover:text-slate-900 transition-colors inline-flex items-center gap-2"
            >
              All services <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`home-service-${s.slug}`}
                className="group relative aspect-[5/6] rounded-2xl overflow-hidden bg-slate-900 transition-transform duration-500 hover:-translate-y-1"
              >
                <VideoTile
                  video={s.video}
                  poster={s.image}
                  alt={s.label}
                  overlay
                />
                <div className="absolute top-4 left-4 right-4 z-10 flex items-center justify-between">
                  <span className="inline-flex items-center gap-2 bg-white/85 backdrop-blur px-2.5 py-1 rounded-full text-[10px] font-mono text-slate-900 tracking-widest uppercase">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="h-8 w-8 rounded-full bg-white/85 backdrop-blur flex items-center justify-center text-slate-900 group-hover:bg-accent-blue group-hover:text-white transition-colors">
                    <ArrowUpRight className="h-3.5 w-3.5" />
                  </span>
                </div>
                <div className="absolute bottom-0 inset-x-0 p-5 md:p-6 z-10">
                  {SERVICE_TAGLINES[s.slug] && (
                    <p className="text-[10px] font-semibold uppercase tracking-widest text-cyan-300 mb-1.5">
                      {SERVICE_TAGLINES[s.slug]}
                    </p>
                  )}
                  <h3 className="font-heading font-black text-2xl md:text-3xl text-white tracking-tighter leading-tight">
                    {s.label}
                  </h3>
                  <p className="text-sm text-white/85 mt-2 leading-relaxed line-clamp-2">
                    {s.short}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works, LIGHT GREY panel */}
      <section
        data-testid="how-it-works"
        className="bg-slate-50 py-24 md:py-32 border-y border-slate-200"
      >
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-accent-blue" />
              <span className="label-eyebrow text-accent-blue">
                How It Works
              </span>
            </div>
            <h2 className="font-heading font-black text-4xl md:text-6xl mt-4 leading-[0.95] tracking-tighter text-slate-900">
              Four steps.{" "}
              <span className="text-slate-400">One clear position.</span>
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {HOW_IT_WORKS.map((step) => (
              <div
                key={step.code}
                data-testid={`how-step-${step.code}`}
                className="bg-white rounded-2xl border border-slate-200 p-6 md:p-7"
              >
                <div className="flex items-baseline gap-3">
                  <span className="font-mono text-xs text-accent-blue">STEP</span>
                  <span className="font-heading font-black text-5xl md:text-6xl text-accent-blue tracking-tighter">
                    {step.code}
                  </span>
                </div>
                <p className="font-heading font-bold text-xl md:text-2xl mt-8 tracking-tight text-slate-900">
                  {step.title}
                </p>
                <ul className="mt-5 space-y-2">
                  {step.points.map((pt) => (
                    <li
                      key={pt}
                      className="text-sm text-slate-600 flex items-start gap-2 leading-relaxed"
                    >
                      <Check className="h-3.5 w-3.5 text-accent-blue mt-1 flex-shrink-0" />
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What you can be known for, DARK SECTION (white + cyan only) */}
      <section
        data-testid="what-known-for"
        className="dark-section py-24 md:py-32 relative overflow-hidden"
        style={{ backgroundColor: "#0f172a" }}
      >
        <div className="absolute inset-0 blueprint-dots opacity-40 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="flex items-center gap-3">
            <span className="h-px w-12 bg-accent-blue" />
            <span className="label-eyebrow text-accent-blue">
              What do you want to be known for?
            </span>
          </div>
          <h2 className="font-heading font-black text-4xl md:text-6xl mt-4 leading-[0.95] tracking-tighter max-w-4xl text-white">
            Pick a position.{" "}
            <span className="text-accent-blue">We&apos;ll help you own it.</span>
          </h2>

          <div className="mt-14 flex flex-wrap gap-3">
            {POSITIONS.map((p) => (
              <Link
                key={p.value}
                to="/positioning-review"
                data-testid={`home-position-${p.value}`}
                className="border border-white/20 hover:border-accent-blue hover:bg-white/5 text-white px-6 py-4 text-base md:text-lg font-heading font-bold uppercase tracking-tight transition-colors group inline-flex items-center gap-2 rounded-full"
              >
                {p.label}
                <ArrowUpRight className="h-4 w-4 text-white/60 group-hover:text-accent-blue transition-colors" />
              </Link>
            ))}
          </div>

          <p className="mt-12 text-white/75 max-w-2xl text-lg leading-relaxed">
            Each position is supported by a tailored mix of video, photography,
            content, social and distribution, built around the audience you
            need to reach.
          </p>
        </div>
      </section>

      {/* Why HighwaysIndustry, WHITE */}
      <section
        data-testid="why-section"
        className="bg-white py-24 md:py-32"
      >
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-accent-blue" />
              <span className="label-eyebrow text-accent-blue">
                Why HighwaysIndustry
              </span>
            </div>
            <h2 className="font-heading font-black text-4xl md:text-6xl mt-4 leading-[0.92] tracking-tighter text-slate-900">
              Built for{" "}
              <span className="text-accent-blue">your sector.</span>
              <br />
              Backed by{" "}
              <span className="text-accent-blue">our audience.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {WHY_REASONS.map((r, i) => (
              <div
                key={r.title}
                data-testid={`why-reason-${i}`}
                className="rounded-2xl border border-slate-200 bg-slate-50 p-6 md:p-8"
              >
                <span className="font-mono text-[10px] text-accent-blue tracking-widest">
                  0{i + 1}
                </span>
                <p className="font-heading font-bold text-xl md:text-2xl mt-5 tracking-tight text-slate-900">
                  {r.title}
                </p>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">
                  {r.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industry Reach stats, DARK SECTION */}
      <section
        data-testid="home-reach"
        className="dark-section py-24 md:py-32 relative"
        style={{ backgroundColor: "#0f172a" }}
      >
        <div className="absolute inset-0 blueprint-dots opacity-30 pointer-events-none" />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-accent-blue" />
              <span className="label-eyebrow text-accent-blue">
                The Big Difference
              </span>
            </div>
            <h2 className="font-heading font-black text-4xl md:text-5xl mt-4 leading-[0.95] tracking-tighter text-white">
              We don&apos;t just create content.
              <br />
              <span className="text-accent-blue">We distribute it too.</span>
            </h2>
            <p className="mt-6 text-white/80 text-base md:text-lg leading-relaxed max-w-md">
              One of the largest dedicated highways audiences in the UK, the
              moat no marketing agency can replicate.
            </p>
            <Link
              to="/industry-reach"
              data-testid="home-reach-cta"
              className="mt-8 inline-flex items-center gap-2 py-2 text-sm font-bold uppercase tracking-widest text-accent-blue hover:text-white transition-colors"
            >
              See our reach <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 gap-4">
            {REACH_STATS_HEADLINE.map((s) => (
              <div
                key={s.label}
                className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 md:p-10"
                data-testid={`home-stat-${s.label.replace(/\s+/g, "-").toLowerCase().replace(/[^a-z0-9-]/g, "")}`}
              >
                <p className="font-heading font-black text-4xl md:text-6xl tracking-tighter text-accent-blue">
                  {s.value}
                </p>
                <p className="text-xs md:text-sm text-white/70 mt-3 leading-relaxed">
                  {s.label}
                </p>
                {s.note && (
                  <p className="text-[10px] md:text-xs text-white/40 mt-1">
                    {s.note}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Case studies preview, WHITE */}
      <section
        data-testid="home-cases"
        className="bg-white py-24 md:py-32"
      >
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="flex items-center gap-3">
                <span className="h-px w-12 bg-accent-blue" />
                <span className="label-eyebrow text-accent-blue">
                  Case Studies
                </span>
              </div>
              <h2 className="font-heading font-black text-4xl md:text-6xl mt-4 leading-[0.95] tracking-tighter text-slate-900 max-w-3xl">
                What good content
                <br />
                actually delivers.
              </h2>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {CASE_STUDIES.slice(0, 3).map((c, i) => (
              <Link
                key={c.slug}
                to={`/case-studies/${c.slug}`}
                data-testid={`home-case-${c.slug}`}
                className="group relative aspect-[4/5] rounded-2xl overflow-hidden bg-slate-900 border border-slate-200 hover:border-accent-blue transition-all"
              >
                {c.thumb && (
                  <img
                    src={c.thumb}
                    alt={c.cardTitle}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                )}
                {/* Legibility gradient */}
                <div
                  aria-hidden
                  className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/50 to-slate-950/10"
                />
                {/* Top: case number + arrow */}
                <div className="absolute top-5 left-5 right-5 flex items-center justify-between">
                  <span className="font-mono text-[10px] text-accent-blue uppercase tracking-widest bg-slate-950/70 backdrop-blur border border-accent-blue/30 px-2.5 py-1 rounded-full">
                    Case {String(i + 1).padStart(2, "0")}
                  </span>
                  <ArrowUpRight className="h-5 w-5 text-white/70 group-hover:text-accent-blue group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all" />
                </div>
                {/* Bottom: text overlay */}
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-7">
                  <span className="block text-[10px] font-heading font-semibold uppercase tracking-widest text-accent-blue">
                    {c.client}
                  </span>
                  <p className="mt-2 font-heading font-black text-2xl md:text-3xl leading-[1.05] tracking-tight text-white">
                    {c.cardTitle}
                  </p>
                  <p className="mt-3 text-[11px] font-heading font-semibold uppercase tracking-widest text-white/50">
                    {c.cardTagline}
                  </p>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-10 flex justify-center">
            <Link
              to="/case-studies"
              data-testid="home-cases-link"
              className="inline-flex items-center gap-2 rounded-full border border-slate-900 hover:bg-slate-900 hover:text-white text-slate-900 px-7 py-3.5 text-sm font-heading font-semibold uppercase tracking-widest transition-colors"
            >
              View all case studies <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Latest features from HighwaysIndustry.com, LIGHT GREY panel */}
      <section
        data-testid="home-latest-features"
        className="bg-slate-50 py-24 md:py-32 border-y border-slate-200"
      >
        <div className="mx-auto max-w-[1400px] px-5 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6">
            <div>
              <div className="flex items-center gap-3">
                <Newspaper size={16} className="text-accent-blue" />
                <span className="label-eyebrow text-accent-blue">
                  Fresh from HighwaysIndustry.com
                </span>
              </div>
              <h2 className="mt-4 font-heading font-black text-4xl md:text-6xl leading-[0.95] tracking-tighter text-slate-900 max-w-3xl">
                Latest features.
                <br />
                <span className="text-slate-400">Straight from our newsroom.</span>
              </h2>
            </div>
            <a
              href="https://www.highwaysindustry.com/category/features/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="home-latest-features-all"
              className="py-2 text-sm font-bold uppercase tracking-widest text-accent-blue hover:text-slate-900 transition-colors inline-flex items-center gap-2"
            >
              See every feature <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>

          {news.length === 0 && !newsError ? (
            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-slate-200 bg-white animate-pulse h-80"
                />
              ))}
            </div>
          ) : newsError ? (
            <div className="mt-14 text-center py-16 text-slate-500">
              <p>Live feed temporarily unavailable.</p>
              <a
                href="https://www.highwaysindustry.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 text-accent-blue underline underline-offset-4"
              >
                Read the latest on HighwaysIndustry.com
                <ArrowUpRight size={14} />
              </a>
            </div>
          ) : (
            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {news.map((item, i) => (
                <a
                  key={item.link || i}
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`home-feature-card-${i}`}
                  className="group rounded-2xl overflow-hidden border border-slate-200 bg-white hover:border-accent-blue hover:shadow-lg transition-all flex flex-col"
                >
                  <div className="aspect-[16/10] w-full bg-slate-100 overflow-hidden">
                    {item.thumbnail && (
                      <img
                        src={`https://images.weserv.nl/?url=${encodeURIComponent(
                          item.thumbnail.replace(/^https?:\/\//, "")
                        )}&w=800&h=500&fit=cover`}
                        alt={item.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                        }}
                      />
                    )}
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {(item.categories || [])
                        .filter(
                          (c) =>
                            !["news", "features"].includes(c.toLowerCase())
                        )
                        .slice(0, 2)
                        .map((cat) => (
                          <span
                            key={cat}
                            className="text-[10px] font-heading font-semibold uppercase tracking-widest text-accent-blue"
                          >
                            {cat.replace(/&amp;/g, "&")}
                          </span>
                        ))}
                    </div>
                    <h3 className="mt-2 font-heading font-bold text-xl md:text-2xl text-slate-900 group-hover:text-accent-blue transition-colors leading-snug line-clamp-3 flex-1">
                      {item.title}
                    </h3>
                    <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
                      <span className="text-xs text-slate-400">
                        {formatDate(item.pubDate)}
                      </span>
                      <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-accent-blue transition-colors" />
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* The Big Difference quote, DARK SECTION */}
      <section
        data-testid="big-difference"
        className="dark-section py-24 md:py-36"
        style={{ backgroundColor: "#0f172a" }}
      >
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-2">
            <Quote className="h-10 w-10 text-accent-blue" />
            <p className="label-eyebrow text-white/60 mt-6">
              The Big Difference
            </p>
          </div>
          <div className="lg:col-span-10">
            <p className="font-heading font-bold text-3xl md:text-5xl leading-[1.05] tracking-tight max-w-4xl text-white">
              Marketing agencies create content and{" "}
              <span className="text-white/50">hope people see it.</span>
            </p>
            <p className="font-heading font-bold text-3xl md:text-5xl leading-[1.05] tracking-tight max-w-4xl mt-4 text-white">
              We create content and{" "}
              <span className="text-accent-blue">
                distribute it through our own industry audience.
              </span>
            </p>
            <p className="mt-8 text-white/70 max-w-2xl leading-relaxed text-lg">
              That&apos;s the difference.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA, WHITE */}
      <section
        data-testid="final-cta"
        className="bg-white py-24 md:py-36"
      >
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 text-center">
          <div className="inline-flex items-center gap-3 mx-auto">
            <span className="h-px w-12 bg-accent-blue" />
            <span className="label-eyebrow text-accent-blue">
              HighwaysIndustry.com
            </span>
            <span className="h-px w-12 bg-accent-blue" />
          </div>
          <h2 className="font-heading font-black text-5xl md:text-7xl lg:text-8xl mt-6 leading-[0.92] tracking-tighter text-slate-900">
            Create great content.
            <br />
            <span className="text-slate-400">Grow your audience.</span>
            <br />
            <span className="text-accent-blue">Build your brand.</span>
          </h2>
          <p className="mt-10 text-slate-500 text-lg max-w-xl mx-auto">
            One discovery call. No pitch decks. Just a straight conversation
            about your business.
          </p>
          <div className="mt-10 flex flex-wrap gap-3 justify-center">
            <Link
              to="/positioning-review"
              data-testid="final-cta-discovery"
              className="bg-primary-blue text-white px-7 py-4 text-sm font-bold uppercase tracking-widest hover:glow-blue transition-all inline-flex items-center gap-2 rounded-full"
            >
              Book A Discovery Call <ArrowUpRight className="h-4 w-4" />
            </Link>
            <Link
              to="/what-we-do"
              className="border border-slate-300 hover:border-accent-blue text-slate-900 px-7 py-4 text-sm font-bold uppercase tracking-widest transition-colors inline-flex items-center gap-2 rounded-full"
            >
              See What We Do
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
